import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Radio } from '@creditornot/cb-radio';
import { Modal, ModalContent, ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import * as R from 'ramda';
import React, { useCallback } from 'react';
import { Field, Form } from 'react-final-form';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import {
  EMPTY_WEEKLY_SCHEDULE_INPUT_ENTRY,
  WeeklyScheduleInput,
  WeeklyScheduleInputEntry,
  fromWeeklyScheduleInputEntriesToWeeklyTimeSpans,
} from 'common/components/weekly-schedules';
import { usePluralization } from 'common/hooks';
import useCurrentUser from 'common/hooks/useCurrentUser';
import validateYupSchema from 'common/utils/validateYupSchema';
import { useBulkChangeVisibilityAndAvailabilityTracking } from 'modules/menu/hooks/telemetryHooks';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';
import { OfferWithSelectionAndAuthor, OfferingApiError } from 'modules/shared-menu/types';

import BulkActionErrorList from '../BulkActionErrorList';
import { BulkActionModalTitle } from '../BulkActionModalTitle';
import { buildBatchAvailabilityVisibilityChangePayload } from '../transformers';
import { useBatchUpdateOffers } from '../useBatchUpdateOffers';
import { updateLocalSelectionData } from '../utils';

type FormValues = {
  useWeeklyAvailability: boolean;
  useWeeklyVisibility: boolean;
  weeklyAvailability: WeeklyScheduleInputEntry[];
  weeklyVisibility: WeeklyScheduleInputEntry[];
};

type Props = {
  onClose: () => void;
  selectedIds: Set<string>;
};

const StyledModalHeader = styled(ModalHeader)`
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const StyledModalContent = styled(ModalContent)`
  padding: 0;
  background-color: ${cbSemanticCssVars.surfaceMain};
  overflow-y: auto;
  max-height: 80vh;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const RadioWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const WeeklyScheduleWrapper = styled.div`
  max-width: 392px;
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  padding-block-start: 24px;
  margin-block-start: 24px;
`;

const StyledRadio = styled(Radio)`
  & + & {
    margin-block-start: 12px;
  }
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const CancelButton = styled(Button).attrs({ variant: 'secondary', size: 'small' })`
  margin-inline-end: 16px;
`;
const SubmitButton = styled(Button).attrs({ variant: 'primary', size: 'small' })``;

const schema = yup
  .object({
    useWeeklyAvailability: yup.boolean().required(),
    useWeeklyVisibility: yup.boolean().required(),
    weeklyAvailability: yup.mixed<FormValues['weeklyAvailability']>().required(),
    weeklyVisibility: yup.mixed<FormValues['weeklyVisibility']>().required(),
  })
  .required();

export type ValidFormValues = yup.Asserts<typeof schema>;

const initialFormValues: FormValues = {
  useWeeklyAvailability: false,
  useWeeklyVisibility: false,
  weeklyAvailability: [EMPTY_WEEKLY_SCHEDULE_INPUT_ENTRY],
  weeklyVisibility: [EMPTY_WEEKLY_SCHEDULE_INPUT_ENTRY],
};

const BulkAvailabilityVisibilityChangeModal = ({ onClose, selectedIds }: Props) => {
  const intl = useIntl();
  const trackChanges = useBulkChangeVisibilityAndAvailabilityTracking([...selectedIds]);
  const { offers, updateSelectionData, selection } = useSelectionData();
  const { data: user } = useCurrentUser();

  const { formatPluralMessage } = usePluralization();

  const onSuccess = useCallback(
    (successfullyUpdatedOffers: OfferWithSelectionAndAuthor[]) => {
      toast.success(
        formatPluralMessage({
          id: 'shared-menu.bulk-actions.toasts.update.success-description',
          count: successfullyUpdatedOffers.length,
        }),
      );
      updateLocalSelectionData(successfullyUpdatedOffers, updateSelectionData);
    },
    [formatPluralMessage, updateSelectionData],
  );

  const onError = useCallback(
    (errors: OfferingApiError[]) => {
      toast.error(
        <BulkActionErrorList
          description={intl.formatMessage({ id: 'shared-menu.bulk-actions.toasts.update.error-description' })}
          errors={errors}
          allOffers={offers}
          selectionPrimaryLanguage={selection.primary_language}
        />,
        { duration: Infinity },
      );
    },
    [intl, offers, selection.primary_language],
  );

  const { executeBatchUpdateOffers, isLoading } = useBatchUpdateOffers();

  const handleConfirm = useCallback(
    async (values: ValidFormValues) => {
      const weeklyAvailability =
        values.useWeeklyAvailability && values.weeklyAvailability.length > 0
          ? fromWeeklyScheduleInputEntriesToWeeklyTimeSpans(values.weeklyAvailability)
          : undefined;
      const weeklyVisibility =
        values.useWeeklyVisibility && values.weeklyVisibility.length > 0
          ? fromWeeklyScheduleInputEntriesToWeeklyTimeSpans(values.weeklyVisibility)
          : undefined;
      const offersToUpdate = offers.filter(offer => selectedIds.has(offer.id));
      const offerPayloads = buildBatchAvailabilityVisibilityChangePayload({
        weeklyAvailability,
        weeklyVisibility,
        offers: offersToUpdate,
        selectionId: selection.id,
        userId: user?._id.$oid ?? '',
      });

      await executeBatchUpdateOffers({
        offerPayloads,
        onSuccess,
        onError,
      });

      trackChanges(weeklyAvailability, weeklyVisibility);
      onClose();
    },
    [
      executeBatchUpdateOffers,
      offers,
      onClose,
      onError,
      onSuccess,
      selectedIds,
      selection.id,
      user?._id.$oid,
      trackChanges,
    ],
  );

  return (
    <Modal open onDismiss={onClose} sizePreset={'centered:large'} disableContentPadding>
      <StyledModalHeader onCloseClick={onClose}>
        <BulkActionModalTitle
          count={selectedIds.size}
          title={<FormattedMessage id="menu.bulkActions.availabilityAndVisibilityChange.title" />}
        />
      </StyledModalHeader>
      <Form<FormValues>
        initialValues={initialFormValues}
        onSubmit={values => {
          const validFormValues = schema.validateSync(values);
          handleConfirm(validFormValues);
        }}
        validate={validateYupSchema(schema)}
      >
        {({ handleSubmit, hasValidationErrors, values }) => (
          <form onSubmit={handleSubmit}>
            <StyledModalContent>
              <FormRow label={intl.formatMessage({ id: 'menu-item.time-available' })} horizontalPadding>
                <ColumnContainer>
                  <Field<FormValues['useWeeklyAvailability']> name="useWeeklyAvailability">
                    {({ input }) => (
                      <RadioWrapper>
                        <StyledRadio checked={!input.value} onChange={() => input.onChange(!input.value)}>
                          <FormattedMessage id="menu-item.time-available-always" />
                        </StyledRadio>
                        <StyledRadio checked={input.value} onChange={() => input.onChange(!input.value)}>
                          <FormattedMessage id="menu-item.time-available-sometimes" />
                        </StyledRadio>
                      </RadioWrapper>
                    )}
                  </Field>

                  {values.useWeeklyAvailability && (
                    <Field<FormValues['weeklyAvailability']> name="weeklyAvailability" isEqual={R.equals}>
                      {({ input, meta }) => (
                        <WeeklyScheduleWrapper>
                          <WeeklyScheduleInput
                            onChange={input.onChange}
                            pristine={!!meta.pristine}
                            select24HoursLabel={intl.formatMessage({ id: 'weeklyScheduleInput.select24Hours' })}
                            value={input.value}
                          />
                        </WeeklyScheduleWrapper>
                      )}
                    </Field>
                  )}
                </ColumnContainer>
              </FormRow>

              <FormRow label={intl.formatMessage({ id: 'menu-item.time-visible' })} horizontalPadding>
                <ColumnContainer>
                  <Field<FormValues['useWeeklyVisibility']> name="useWeeklyVisibility">
                    {({ input }) => (
                      <RadioWrapper>
                        <StyledRadio checked={!input.value} onChange={() => input.onChange(!input.value)}>
                          <FormattedMessage id="menu-item.time-visible-always" />
                        </StyledRadio>
                        <StyledRadio checked={input.value} onChange={() => input.onChange(!input.value)}>
                          <FormattedMessage id="menu-item.time-visible-sometimes" />
                        </StyledRadio>
                      </RadioWrapper>
                    )}
                  </Field>

                  {values.useWeeklyVisibility && (
                    <Field<FormValues['weeklyVisibility']> name="weeklyVisibility" isEqual={R.equals}>
                      {({ input, meta }) => (
                        <WeeklyScheduleWrapper>
                          <WeeklyScheduleInput
                            onChange={input.onChange}
                            pristine={!!meta.pristine}
                            select24HoursLabel={intl.formatMessage({ id: 'weeklyScheduleInput.select24Hours' })}
                            value={input.value}
                          />
                        </WeeklyScheduleWrapper>
                      )}
                    </Field>
                  )}
                </ColumnContainer>
              </FormRow>
            </StyledModalContent>
            <StyledModalFooter>
              <CancelButton onClick={onClose}>
                <FormattedMessage id="state.cancel" />
              </CancelButton>
              <SubmitButton
                disabled={hasValidationErrors || isLoading}
                loading={isLoading}
                type="submit"
                onClick={handleSubmit}
              >
                <FormattedMessage id="state.save" />
              </SubmitButton>
            </StyledModalFooter>
          </form>
        )}
      </Form>
    </Modal>
  );
};

export default BulkAvailabilityVisibilityChangeModal;
